import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import {
  Badge,
  Button,
  Flex,
  Stack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import {
  promptKeys,
  useGetAllPrompts,
  useCreatePrompt,
  useUpdatePrompt,
  useDeletePrompt,
} from "../../../hooks/query/prompt";
import { PromptModal, PromptsTable } from "./components";
import PageHeader from "../../../components/text/PageHeader";

function Prompts() {
  const { t } = useTranslation("translation");
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();

  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  const { data: prompts = [], isLoading, isFetching } = useGetAllPrompts({
    onError: (err) => handleErrors(err),
  });

  const { mutate: createPrompt, isLoading: isCreating } = useCreatePrompt({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, "Prompt created successfully");
      queryClient.invalidateQueries(promptKeys.all());
      closeModal();
    },
    onError: (err) => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: updatePrompt, isLoading: isUpdating } = useUpdatePrompt({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, "Prompt updated successfully");
      queryClient.invalidateQueries(promptKeys.all());
      closeModal();
    },
    onError: (err) => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: deletePrompt } = useDeletePrompt({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, "Prompt deleted successfully");
      queryClient.invalidateQueries(promptKeys.all());
      closeModal();
    },
    onError: (err) => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const handleErrors = (error) => {
    const errorMessage = error.response?.data?.message || error.message || "An error occurred";
    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const handleAddNew = () => {
    setSelectedPrompt(null);
    onModalOpen();
  };

  const closeModal = () => {
    setSelectedPrompt(null);
    onModalClose();
  };

  const handleRowClick = (row) => {
    setSelectedPrompt(JSON.parse(JSON.stringify(row.original)));
    onModalOpen();
  };

  const handleDuplicate = () => {
    setSelectedPrompt((prev) => ({ content: prev.content }));
  };

  const handleDelete = (promptId) => {
    if (window.confirm("Are you sure you want to delete this prompt?")) {
      deletePrompt(promptId);
    }
  };

  const handleModalSubmit = (promptId, data) => {
    if (promptId) {
      updatePrompt({ id: promptId, data });
    } else {
      createPrompt(data);
    }
  };

  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
    },
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <div style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.original.description || "-"}
        </div>
      ),
      size: 300,
    },
    {
      header: "Universal",
      accessorKey: "isUniversal",
      cell: ({ row }) => (
        <Badge colorScheme={row.original.isUniversal ? "green" : "gray"}>
          {row.original.isUniversal ? "Yes" : "No"}
        </Badge>
      ),
      size: 100,
    },
  ];

  return (
    <Page>
      <Stack spacing={10} p={4} bg="pureWhite" marginLeft="15%" marginRight="15%">
        <Flex justify="space-between" align="flex-start">
          <PageHeader
            title="Prompt Management"
            subTitle="Create and manage AI prompts used across the platform for disclosures, chat, reports, and AI reviews."
          />
          <Button variant="primary" borderRadius="full" onClick={handleAddNew} flexShrink={0} mt={2}>
            + Create Prompt
          </Button>
        </Flex>

        {isModalOpen && (
          <PromptModal
            prompt={selectedPrompt}
            isOpen={isModalOpen}
            onClose={closeModal}
            onSubmit={handleModalSubmit}
            onDelete={selectedPrompt?.id ? () => handleDelete(selectedPrompt.id) : undefined}
            onDuplicate={selectedPrompt?.id ? handleDuplicate : undefined}
            isSubmitting={isCreating || isUpdating}
          />
        )}

        <PromptsTable
          data={prompts}
          columns={columns}
          isLoading={isLoading || isFetching}
          onRowClick={handleRowClick}
        />
      </Stack>
    </Page>
  );
}

export default WithAuthorization(Prompts);
